/**
 * videoModelResolutions.js — resolution options per video model (Feb 2026).
 *
 * The admin backend's model catalog may expose a `resolutions` /
 * `supported_resolutions` / `resolution_options` array. When present we
 * use that verbatim. When absent (older catalog entries, or `videoModel`
 * passed as a plain string id) we fall back to a hardcoded map keyed by
 * substring match on the model id — covers Seedance / Kling / Wan /
 * Sora / Gemini / LTX / Pixverse / Minimax families.
 */

const PROVIDER_RESOLUTION_MAP = [
  // [regex matched against lowercased model id, [resolutions...], defaultIndex]
  [/seedance/,     ['480p', '720p', '1080p'], 1],
  [/wan[-_]?2/,    ['480p', '580p', '720p'],  0],
  [/sora/,         ['auto', '720p'],          0],
  [/ltx/,          ['720p', '1080p'],         0],
  [/kling/,        ['720p', '1080p'],         0],
  [/pixverse/,     ['540p', '720p', '1080p'], 1],
  [/minimax|hailuo/, ['720p', '1080p'],       0],
  [/veo/,          ['720p', '1080p'],         0],
  [/gemini/,       ['720p', '1080p'],         0],
];

const DEFAULT_RESOLUTIONS = ['480p', '720p', '1080p'];

// Admin catalog shape: ["720p"] or [{ resolution: "480p", credits_per_second: 30 }, ...]
const resolutionLabel = (r) => (typeof r === 'string' ? r : (r?.resolution || r?.label || r?.value || r?.name || '')).toString().trim();

const normaliseResolutionList = (list) => {
  if (!Array.isArray(list)) return null;
  const out = list.map(resolutionLabel).filter((s) => s.length > 0);
  return out.length > 0 ? Array.from(new Set(out)) : null;
};

const catalogListOf = (model) => normaliseResolutionList(
  model?.resolutions || model?.supported_resolutions || model?.resolution_options
);

// Model ids from the admin catalog are UUIDs — match the family on name/provider too.
const familyText = (modelOrId) => {
  const model = typeof modelOrId === 'object' && modelOrId ? modelOrId : null;
  return [model?.id, model?.model_id, model?.key, model?.name, model?.provider, typeof modelOrId === 'string' ? modelOrId : '']
    .filter(Boolean).join(' ').toLowerCase();
};

const familyFallback = (modelOrId) => {
  const text = familyText(modelOrId);
  for (const [rx, options, defaultIdx] of PROVIDER_RESOLUTION_MAP) {
    if (rx.test(text)) return { options, def: options[defaultIdx] || options[0] };
  }
  return { options: DEFAULT_RESOLUTIONS, def: '720p' };
};

/**
 * Resolutions offered by the model. The admin catalog list is used verbatim
 * when present; the family map is only a fallback when the catalog is empty.
 */
export const getResolutionOptionsForModel = (modelOrId) => {
  const model = typeof modelOrId === 'object' && modelOrId ? modelOrId : null;
  return catalogListOf(model) || familyFallback(modelOrId).options;
};

/** Admin catalog `default_resolution` when it is one of the offered options; else family default. */
export const getDefaultResolutionForModel = (modelOrId) => {
  const model = typeof modelOrId === 'object' && modelOrId ? modelOrId : null;
  const options = getResolutionOptionsForModel(modelOrId);
  // The user's pick on the AI Toolkit tile beats the catalog default.
  const toolkitPick = getToolkitResolution(modelOrId);
  if (toolkitPick && options.includes(toolkitPick)) return toolkitPick;
  const catalogDefault = typeof model?.default_resolution === 'string' ? model.default_resolution.trim() : '';
  if (catalogDefault && options.includes(catalogDefault)) return catalogDefault;
  const { def } = familyFallback(modelOrId);
  return options.includes(def) ? def : (options.includes('720p') ? '720p' : options[0]);
};

/** Credits/second per resolution from the catalog: { "480p": 30, ... } (empty when unknown). */
export const getResolutionCreditsForModel = (modelOrId) => {
  const model = typeof modelOrId === 'object' && modelOrId ? modelOrId : null;
  const out = {};
  for (const r of (Array.isArray(model?.resolutions) ? model.resolutions : [])) {
    const label = resolutionLabel(r);
    const credits = Number(r?.credits_per_second);
    if (label && Number.isFinite(credits)) out[label] = credits;
  }
  return out;
};

// ─── AI Toolkit resolution preference (per model id), set on the toolkit tiles ───
const TOOLKIT_RES_KEY = 'selectedAIResolutions';

export const getToolkitResolutions = () => {
  try { return JSON.parse(localStorage.getItem(TOOLKIT_RES_KEY) || '{}') || {}; } catch { return {}; }
};

export const getToolkitResolution = (modelOrId) => {
  const prefs = getToolkitResolutions();
  const model = typeof modelOrId === 'object' && modelOrId ? modelOrId : null;
  const ids = [model?.key, model?.id, model?.model_id, typeof modelOrId === 'string' ? modelOrId : null].filter(Boolean);
  for (const id of ids) if (typeof prefs[id] === 'string' && prefs[id]) return prefs[id];
  return null;
};

export const setToolkitResolution = (modelId, resolution) => {
  if (!modelId) return;
  const prefs = getToolkitResolutions();
  if (resolution) prefs[modelId] = resolution; else delete prefs[modelId];
  try { localStorage.setItem(TOOLKIT_RES_KEY, JSON.stringify(prefs)); } catch { /* quota */ }
};

/** Catalog list only (no family fallback) — what the AI Toolkit tile shows. */
export const getCatalogResolutions = (model) => catalogListOf(model) || [];
